import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LayoutDashboard, Wallet, HardHat, LogOut, Lock } from 'lucide-react';

const HomeHub = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      title: 'Management Hub',
      description: 'Projects, teams, tasks & milestones',
      icon: LayoutDashboard,
      path: '/dashboard',
      enabled: user?.role === 'ceo',
      color: 'from-white/10 to-white/5',
      iconColor: 'text-white',
      borderColor: 'border-neutral-700 hover:border-neutral-500',
    },
    {
      title: 'Finance',
      description: 'Financial ledger & reports',
      icon: Wallet,
      path: '/finance',
      enabled: false,
      color: 'from-white/5 to-white/[0.02]',
      iconColor: 'text-neutral-500',
      borderColor: 'border-neutral-800',
    },
    {
      title: 'Engineering',
      description: 'Tasks & project milestones',
      icon: HardHat,
      path: '/engineering',
      enabled: user?.role === 'ceo',
      color: 'from-white/10 to-white/5',
      iconColor: 'text-white',
      borderColor: 'border-neutral-700 hover:border-neutral-500',
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a' }} data-testid="home-hub-page">
      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-16">
          <div className="flex items-center gap-4">
            <img src="/cremona-logo.jpg" alt="Cremona" className="w-12 h-12 rounded-md object-contain" />
            <div>
              <h1 className="text-xl font-bold text-white tracking-wide">CREMONA</h1>
              <p className="text-[10px] tracking-[0.25em] text-neutral-500 uppercase">Structure Engineering</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-neutral-400" data-testid="user-greeting">
              {user?.name}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-neutral-500 hover:text-white hover:bg-neutral-800"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>

        {/* Welcome */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            Welcome back, {user?.name}
          </h2>
          <p className="text-neutral-500">Select a module to get started</p>
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {menuItems.map((item) => (
            <Card
              key={item.title}
              className={`relative overflow-hidden transition-all duration-300 cursor-pointer bg-gradient-to-br ${item.color} border ${item.borderColor} ${
                item.enabled
                  ? 'hover:scale-[1.02] hover:shadow-lg hover:shadow-white/5'
                  : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={() => item.enabled && navigate(item.path)}
              data-testid={`menu-${item.title.toLowerCase().replace(/\s/g, '-')}`}
            >
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-12 h-12 rounded-lg bg-neutral-800/80 flex items-center justify-center`}>
                    <item.icon className={`w-6 h-6 ${item.iconColor}`} />
                  </div>
                  {!item.enabled && (
                    <div className="flex items-center gap-1.5 text-neutral-600">
                      <Lock className="w-3 h-3" />
                      <span className="text-[10px] tracking-wider uppercase">Coming Soon</span>
                    </div>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-neutral-500">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Footer hint */}
        <p className="text-center text-neutral-700 text-xs mt-16">
          More modules will be available as they are developed
        </p>
      </div>
    </div>
  );
};

export default HomeHub;
